import { RefObject, useEffect, useRef } from 'react';

interface UseInfiniteScrollTriggerParams {
  /** Called when the sentinel comes into view. Must be safe to call repeatedly. */
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  /** Number of items rendered so far; used to re-check visibility after each page. */
  itemCount: number;
  /** Scrollable ancestor. Defaults to the viewport. */
  rootRef?: RefObject<HTMLElement>;
  /** How far before the sentinel enters the view the next page should be requested. */
  rootMargin?: string;
}

/**
 * Infinite-scroll trigger based on IntersectionObserver. Attach the returned ref to an element placed after
 * the list. Unlike a scroll listener it also fires when the first page does not fill the container
 * (so there is nothing to scroll), and re-checks after every page so a still-visible sentinel keeps loading.
 */
export const useInfiniteScrollTrigger = ({
  onLoadMore,
  hasMore,
  isLoadingMore,
  itemCount,
  rootRef,
  rootMargin = '0px 0px 120px 0px',
}: UseInfiniteScrollTriggerParams) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoadingMore || typeof IntersectionObserver === 'undefined') return undefined;

    // A fresh observer reports the current state straight away, which covers the "already visible" case.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root: rootRef?.current ?? null, rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, itemCount, onLoadMore, rootRef, rootMargin]);

  return sentinelRef;
};
